import {useCallback, useMemo} from 'react';

import {useJSONTreeSearchDebouncedContext} from './JSONTreeSearchContextProvider';
import {filterJSON, type FilterJSONOptions} from './util/filterJSON';
import {type JSONNode} from './util/jsonTypes';
import {unflattenJSON} from './util/unflattenJSON';

// The default node transform function is unflattenJSON.
// If you pass in a custom node transform function, it will run *in addition to* this default node transform function.
export const useFilteredJSON = ({
  json,
  filterOptions,
}: {
  json?: JSONNode;
  filterOptions?: FilterJSONOptions;
}) => {
  const {isSearching, getIsSearchMatch} = useJSONTreeSearchDebouncedContext();
  const {
    nodeTransformFunction: customNodeTransformFunction,
    stringSearchFunction: customStringSearchFunction,
    excludeList,
  } = filterOptions ?? {};

  const nodeTransformFunction = useCallback(
    (node: JSONNode) => {
      const propsTransformedNode =
        customNodeTransformFunction == null
          ? node
          : customNodeTransformFunction(node);

      return unflattenJSON({
        node: propsTransformedNode,
        delimiter: undefined,
      });
    },
    [customNodeTransformFunction]
  );

  // The default search function is getIsSearchMatch from JSONTreeSearchDebouncedContext.
  // If you pass in a custom search function, it will run *instead of* this default search function.
  const stringSearchFunction = useCallback(
    (str: string) => {
      if (!isSearching) {
        return true;
      }
      return customStringSearchFunction?.(str) ?? getIsSearchMatch(str);
    },
    [customStringSearchFunction, getIsSearchMatch, isSearching]
  );

  return useMemo(
    () =>
      filterJSON({
        node: json ?? {},
        options: {
          excludeList,
          stringSearchFunction,
          nodeTransformFunction,
        },
      }),
    [json, nodeTransformFunction, excludeList, stringSearchFunction]
  );
};
